import { previewPath, type PreviewError, type PreviewFormat } from '../api';

/*
 * Stream preview (docs/m15-live-stats.md 1.1, 3.2, 3.4): the latest keyframe
 * of the stream, as the node holds it, decoded by this browser.
 *
 *   WebCodecs  GET /api/live/preview?format=annexb → VideoDecoder (codec from
 *              X-Codec, no description: the input is Annex B) → canvas
 *   MSE        GET …&format=fmp4 → a hidden <video> fed by MediaSource
 *              (ManagedMediaSource on iOS Safari) → canvas
 *   neither    "Preview isn't available in this browser", no requests at all
 *
 * Requests only while the API says preview_available. One every 5 s (10 s on a
 * slow link), none while the tab is hidden. 429 waits for Retry-After (the
 * server stretches the interval for big frames; two tabs share the limit), 502
 * node_unreachable keeps the frame and tries on the next tick.
 * The old frame stays on the canvas until a new one is decoded, so a failed
 * frame only makes the shown one older. Components read a PreviewSnapshot
 * and never touch the network or the decoder.
 */

export type PreviewMode = 'webcodecs' | 'mse' | 'unsupported';

export interface PreviewSnapshot {
  mode: PreviewMode;
  /** the tab is hidden: no requests until it is visible again */
  paused: boolean;
  /** the API says the stream is not live: hide the preview */
  offline: boolean;
  /** a frame is on the canvas */
  hasFrame: boolean;
  /** when the node received the frame on the canvas, wall clock ms (X-Frame-Age-Ms + time since the response) */
  frameAt?: number;
}

export const PREVIEW_INTERVAL_MS = 5000;
export const PREVIEW_SLOW_INTERVAL_MS = 10_000;
/** a frame older than this is shown as old */
export const PREVIEW_OLD_MS = 20_000;
/** a response slower than this switches to the slow interval */
const SLOW_RESPONSE_MS = 2000;
const MSE_TIMEOUT_MS = 5000;
/** MSE attempts that never produced a frame before we give up on it */
const MSE_GIVE_UP = 3;
const DEFAULT_CODEC = 'avc1.640028';

type MediaSourceCtor = typeof MediaSource;

function mediaSourceCtor(): MediaSourceCtor | undefined {
  const w = window as unknown as { MediaSource?: MediaSourceCtor; ManagedMediaSource?: MediaSourceCtor };
  return w.MediaSource ?? w.ManagedMediaSource;
}

function hasWebCodecs(): boolean {
  return typeof window.VideoDecoder === 'function' && typeof window.EncodedVideoChunk === 'function';
}

function hasMse(): boolean {
  const M = mediaSourceCtor();
  try {
    return !!M && M.isTypeSupported(`video/mp4; codecs="${DEFAULT_CODEC}"`);
  } catch {
    return false;
  }
}

/** The best decoding path this browser offers, checked once per preview. */
export function detectMode(): PreviewMode {
  if (hasWebCodecs()) return 'webcodecs';
  if (hasMse()) return 'mse';
  return 'unsupported';
}

/** Slow link: Network Information API (Chromium) says so, or the user asked to save data. */
function slowConnection(): boolean {
  const c = (navigator as unknown as { connection?: { effectiveType?: string; saveData?: boolean } }).connection;
  return !!c && (c.saveData === true || c.effectiveType === 'slow-2g' || c.effectiveType === '2g' || c.effectiveType === '3g');
}

/** Retry-After as delay seconds or an HTTP date; a missing or broken header waits one interval. */
export function retryAfterMs(h: string | null, now: number): number {
  if (!h) return PREVIEW_INTERVAL_MS;
  const s = Number(h);
  if (Number.isFinite(s)) return Math.max(0, s * 1000);
  const t = Date.parse(h);
  return Number.isFinite(t) ? Math.max(0, t - now) : PREVIEW_INTERVAL_MS;
}

/** Draws the whole source into the canvas: letterboxed, never cropped or stretched. */
export function drawContain(canvas: HTMLCanvasElement, src: CanvasImageSource, sw: number, sh: number) {
  const ctx = canvas.getContext('2d');
  if (!ctx || !sw || !sh) return;
  const cw = canvas.width;
  const ch = canvas.height;
  const k = Math.min(cw / sw, ch / sh);
  const dw = Math.round(sw * k);
  const dh = Math.round(sh * k);
  ctx.clearRect(0, 0, cw, ch);
  ctx.drawImage(src, Math.floor((cw - dw) / 2), Math.floor((ch - dh) / 2), dw, dh);
}

/** One Annex B keyframe → one VideoFrame. A fresh decoder per frame: frames are 5 s apart and independent. */
function decodeKeyframe(data: ArrayBuffer, codec: string, width: number, height: number): Promise<VideoFrame> {
  return new Promise((resolve, reject) => {
    let frame: VideoFrame | undefined;
    let failed = false;
    const dec = new VideoDecoder({
      output: (f) => {
        if (frame) f.close();
        else frame = f;
      },
      error: (e) => {
        failed = true;
        reject(e);
      },
    });
    const close = () => {
      if (dec.state !== 'closed') dec.close();
    };
    try {
      dec.configure({ codec, codedWidth: width || undefined, codedHeight: height || undefined, optimizeForLatency: true });
      dec.decode(new EncodedVideoChunk({ type: 'key', timestamp: 0, data }));
    } catch (e) {
      close();
      reject(e);
      return;
    }
    dec.flush().then(
      () => {
        close();
        if (frame) resolve(frame);
        else if (!failed) reject(new Error('decoder gave no frame'));
      },
      (e) => {
        close();
        frame?.close();
        if (!failed) reject(e);
      },
    );
  });
}

function once(t: EventTarget, name: string): Promise<Event> {
  return new Promise((resolve) => t.addEventListener(name, resolve, { once: true }));
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms);
    p.then(
      (v) => (clearTimeout(t), resolve(v)),
      (e) => (clearTimeout(t), reject(e)),
    );
  });
}

/**
 * One fMP4 fragment (one frame at t = 0) → a hidden muted video showing it.
 * The caller draws it and calls dispose(); the media source is torn down for
 * every frame.
 */
async function decodeFragment(data: ArrayBuffer, codec: string): Promise<{ video: HTMLVideoElement; dispose: () => void }> {
  const M = mediaSourceCtor()!;
  const ms = new M();
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  // ManagedMediaSource (iOS Safari) only attaches when remote playback is off.
  (video as unknown as { disableRemotePlayback: boolean }).disableRemotePlayback = true;
  video.setAttribute('aria-hidden', 'true');
  video.tabIndex = -1;
  video.style.cssText = 'position:fixed;left:0;top:0;width:2px;height:2px;opacity:0;pointer-events:none';
  const url = URL.createObjectURL(ms);
  const dispose = () => {
    video.removeAttribute('src');
    video.load();
    video.remove();
    URL.revokeObjectURL(url);
  };
  try {
    const opened = once(ms, 'sourceopen');
    video.src = url;
    document.body.append(video);
    await withTimeout(
      (async () => {
        await opened;
        const sb = ms.addSourceBuffer(`video/mp4; codecs="${codec}"`);
        const done = once(sb, 'updateend');
        sb.appendBuffer(data);
        await done;
        if (ms.readyState === 'open') {
          try {
            ms.endOfStream();
          } catch {
            // ended already
          }
        }
        if (video.readyState < 2) await once(video, 'loadeddata');
        // Safari may not paint the frame after loadeddata: seek to it.
        const seeked = once(video, 'seeked');
        video.currentTime = 0;
        await Promise.race([seeked, new Promise((r) => setTimeout(r, 500))]);
        if (!video.videoWidth) throw new Error('no picture');
      })(),
      MSE_TIMEOUT_MS,
    );
    return { video, dispose };
  } catch (e) {
    dispose();
    throw e;
  }
}

/**
 * Fetches and draws the preview of one stream into one canvas. start() once,
 * setAvailable() as the API's preview_available changes, stop() on unmount.
 */
export class PreviewController {
  private snap: PreviewSnapshot;
  private stopped = true;
  private available = false;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private abort: AbortController | undefined;
  private running = false;
  private notBefore = 0;
  private slowResponse = false;
  /** codec strings this browser decoded with WebCodecs at least once */
  private decoded = new Set<string>();
  private supported = new Map<string, boolean>();
  private mseFailures = 0;
  private mseWorked = false;

  constructor(
    private canvas: HTMLCanvasElement,
    private stream: string,
    private onChange: (s: PreviewSnapshot) => void,
  ) {
    this.snap = { mode: detectMode(), paused: document.hidden, offline: false, hasFrame: false };
  }

  get snapshot(): PreviewSnapshot {
    return this.snap;
  }

  private set(p: Partial<PreviewSnapshot>) {
    this.snap = { ...this.snap, ...p };
    this.onChange(this.snap);
  }

  private onVisibility = () => {
    if (document.hidden) {
      this.clear();
      this.abort?.abort();
      this.set({ paused: true });
    } else {
      this.set({ paused: false });
      this.clear();
      void this.tick();
    }
  };

  start(available: boolean) {
    this.stopped = false;
    this.available = available;
    document.addEventListener('visibilitychange', this.onVisibility);
    this.onChange(this.snap);
    this.schedule(0);
  }

  stop() {
    this.stopped = true;
    this.clear();
    this.abort?.abort();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  setAvailable(available: boolean) {
    if (available === this.available) return;
    this.available = available;
    if (available && !this.running) {
      this.clear();
      void this.tick();
    }
  }

  private clear() {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }

  private schedule(ms: number) {
    this.clear();
    if (this.stopped) return;
    this.timer = setTimeout(() => void this.tick(), ms);
  }

  private interval(): number {
    return this.slowResponse || slowConnection() ? PREVIEW_SLOW_INTERVAL_MS : PREVIEW_INTERVAL_MS;
  }

  private async tick() {
    this.timer = undefined;
    if (this.stopped || this.running) return;
    if (document.hidden) {
      if (!this.snap.paused) this.set({ paused: true });
      return; // visibilitychange resumes
    }
    if (this.snap.mode === 'unsupported' || !this.available) return; // setAvailable resumes
    const wait = this.notBefore - Date.now();
    if (wait > 0) {
      this.schedule(wait);
      return;
    }
    this.running = true;
    const t0 = Date.now();
    let again: number | undefined;
    try {
      again = await this.fetchFrame();
    } catch {
      // network error or a broken frame: keep the old frame, it just gets older
    } finally {
      this.running = false;
    }
    if (this.stopped || document.hidden) return;
    const took = Date.now() - t0;
    if (again !== undefined) this.schedule(again);
    else this.schedule(Math.max(0, this.interval() - took));
  }

  /** One request. Returns a delay to override the normal interval (fallback, 429). */
  private async fetchFrame(): Promise<number | undefined> {
    const mode = this.snap.mode;
    const format: PreviewFormat = mode === 'mse' ? 'fmp4' : 'annexb';
    this.abort = new AbortController();
    const t0 = Date.now();
    const r = await fetch(previewPath(this.stream, format), {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: this.abort.signal,
    });
    if (r.status === 429) {
      this.notBefore = Date.now() + retryAfterMs(r.headers.get('Retry-After'), Date.now());
      return Math.max(this.notBefore - Date.now(), 0);
    }
    if (r.status === 404) {
      const err = ((await r.json().catch(() => ({}))) as { error?: PreviewError }).error;
      if (err === 'offline') this.set({ offline: true });
      else if (this.snap.offline) this.set({ offline: false });
      return undefined;
    }
    if (!r.ok) return undefined; // 502 node_unreachable and the like: the frame stays and ages
    const codec = r.headers.get('X-Codec') || DEFAULT_CODEC;
    const width = Number(r.headers.get('X-Width')) || 0;
    const height = Number(r.headers.get('X-Height')) || 0;
    const age = Math.max(0, Number(r.headers.get('X-Frame-Age-Ms')) || 0);
    const data = await r.arrayBuffer();
    const receivedAt = Date.now();
    this.slowResponse = receivedAt - t0 > SLOW_RESPONSE_MS;
    if (this.snap.offline) this.set({ offline: false });
    if (!data.byteLength) return undefined;
    const frameAt = receivedAt - age;

    if (mode === 'webcodecs') {
      if (!(await this.webCodecsSupports(codec, width, height))) return this.fallBack();
      let frame: VideoFrame;
      try {
        frame = await decodeKeyframe(data, codec, width, height);
      } catch (e) {
        // Supported on paper but never decoded: this browser can't take Annex B for this codec.
        if (!this.decoded.has(codec)) return this.fallBack();
        throw e;
      }
      try {
        if (!this.stopped) drawContain(this.canvas, frame, frame.displayWidth, frame.displayHeight);
      } finally {
        frame.close();
      }
      this.decoded.add(codec);
    } else {
      let shown: Awaited<ReturnType<typeof decodeFragment>>;
      try {
        shown = await decodeFragment(data, codec);
      } catch (e) {
        if (!this.mseWorked && ++this.mseFailures >= MSE_GIVE_UP) {
          this.set({ mode: 'unsupported' });
          return undefined;
        }
        throw e;
      }
      try {
        if (!this.stopped) drawContain(this.canvas, shown.video, shown.video.videoWidth, shown.video.videoHeight);
      } finally {
        shown.dispose();
      }
      this.mseWorked = true;
    }
    if (!this.stopped) this.set({ hasFrame: true, frameAt });
    return undefined;
  }

  private async webCodecsSupports(codec: string, width: number, height: number): Promise<boolean> {
    const known = this.supported.get(codec);
    if (known !== undefined) return known;
    let ok = false;
    try {
      ok = !!(await VideoDecoder.isConfigSupported({ codec, codedWidth: width || undefined, codedHeight: height || undefined })).supported;
    } catch {
      ok = false;
    }
    this.supported.set(codec, ok);
    return ok;
  }

  /** WebCodecs can't take this stream: MSE if the browser has it, else no preview. Retries at once. */
  private fallBack(): number | undefined {
    if (hasMse()) {
      this.set({ mode: 'mse' });
      return 0;
    }
    this.set({ mode: 'unsupported' });
    return undefined;
  }
}
